import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import { AButton, ATitle, Loading } from '../../../components';
import { Form, Row, Table } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import {
  DislikeOutlined,
  FileProtectOutlined,
  LeftCircleOutlined,
  LikeOutlined,
} from '@ant-design/icons';
import {
  ILinkedTreatmentProduct,
  IPaymentMethod,
  IRetailTreatmentItem,
  IProcedureInsert,
  ITreatment,
  ITreatmentItem,
} from '../../../shared/interfaces';
import { currency } from '../../../shared/utils/functions';
import ConfirmTreatment from './Confirm';
import RefuseTreatment from './Refuse';
import { getSelectPaymentsMethods, getTreatment } from '../../../http';

const TreatmentApprovalOrNot: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [treatment, setTreatment] = useState<ITreatment | null>(null);
  const [treatmentItems, setTreatmentItems] = useState<ITreatmentItem[]>([]);
  const [paymentsMethods, setPaymentsMethods] = useState<IPaymentMethod[]>([]);
  const [linkedProducts, setLinkedProducts] = useState<
    ILinkedTreatmentProduct[]
  >([]);
  const [retailItems, setRetailItems] = useState<IRetailTreatmentItem[]>([]);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [modalConfirm, setModalConfirm] = useState<boolean>(false);
  const [modalRefuse, setModalRefuse] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const handleSubmit = useCallback(() => {
    setModalConfirm(true);
  }, []);

  const handleCancel = useCallback(() => {
    setModalRefuse(true);
  }, []);

  const loadTreatment = useCallback(async () => {
    await getTreatment(id)
      .then((data) => {
        const { treatment, items, retail_items, linked_products } = data;
        setTreatment(treatment);
        setTreatmentItems(items);
        setLinkedProducts(linked_products ?? []);
        setRetailItems(retail_items ?? []);
        setDisabled(treatment.approval ?? false);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description:
            error.response.data.message ?? 'Erro ao listar os tratamentos.',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const listPaymentsMethods = useCallback(async () => {
    await getSelectPaymentsMethods()
      .then((data) => setPaymentsMethods(data))
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: 'Erro ao listar formas de pagamento',
        });
      });
  }, []);

  const renderModalConfirm = () => {
    if (modalConfirm) {
      return (
        <ConfirmTreatment
          open={modalConfirm}
          close={() => setModalConfirm(false)}
          treatment={treatment}
          paymentsMethods={paymentsMethods}
          setApproval={null}
          treatment_id={id}
          retailProducts={retailItems.map((item) => ({
            id: item.id,
            product_id: item.product_id,
            quantity: Number(item.quantity || 0),
            unit_price: Number(item.unit_price || 0),
            total_price: Number(item.total_price || 0),
          }))}
          treatmentTotal={Math.max(
            0,
            Number(treatment?.total || 0) - Number(treatment?.discount || 0),
          )}
          totalAmount={
            Math.max(
              0,
              Number(treatment?.total || 0) - Number(treatment?.discount || 0),
            ) +
            retailItems.reduce(
              (sum, item) => sum + Number(item.total_price || 0),
              0,
            )
          }
        />
      );
    }
  };

  const renderModalRefuse = () => {
    if (modalRefuse) {
      return (
        <RefuseTreatment
          open={modalRefuse}
          close={() => setModalRefuse(false)}
          treatment={treatment}
        />
      );
    }
  };

  const renderButtonsApprovalRefuse = () => {
    if (!treatment?.approval) {
      return (
        <>
          <AButton
            type="primary"
            danger
            onClick={handleCancel}
            disabled={disabled || treatment?.approval === false}
            icon={<DislikeOutlined />}
          >
            Recusar
          </AButton>
          <AButton
            type="primary"
            onClick={handleSubmit}
            disabled={disabled}
            icon={<LikeOutlined />}
          >
            Aprovar
          </AButton>
        </>
      );
    }
  };

  useEffect(() => {
    loadTreatment();
    listPaymentsMethods();
  }, [id]);

  if (loading) return <Loading load />;

  const treatmentNetTotal = Math.max(
    0,
    Number(treatment?.total || 0) - Number(treatment?.discount || 0),
  );
  const retailTotal = retailItems.reduce(
    (sum, item) => sum + Number(item.total_price || 0),
    0,
  );
  const linkedProductsTotal = linkedProducts.reduce(
    (sum, item) => sum + Number(item.total_price || 0),
    0,
  );

  return (
    <>
      <ATitle icon={<FileProtectOutlined />}>
        {treatment?.type === 1 ? 'Tratamento' : 'Orçamento'} Nº{' '}
        {treatment?.code}
      </ATitle>
      <Table
        rowKey="id"
        style={{ width: '100%', marginTop: 20 }}
        scroll={{ y: 350 }}
        columns={[
          {
            title: '#',
            width: 75,
            render: (value, record, index) => <>{index + 1}</>,
          },
          {
            title: 'Procedimento',
            dataIndex: 'procedure_name',
            width: 200,
          },
          {
            title: 'Qtd.',
            dataIndex: 'quantity',
            width: 75,
          },
          {
            title: 'Valor UN.',
            width: 130,
            dataIndex: 'value',
            render: (value: number) => <>{currency(value)}</>,
          },
          {
            title: 'Desconto',
            width: 130,
            render: (cell: IProcedureInsert) => (
              <>{currency(cell.discount * cell.quantity)}</>
            ),
          },
          {
            title: 'Total Final',
            width: 130,
            render: (cell: IProcedureInsert) => (
              <>
                {currency(
                  cell.value * cell.quantity - cell.discount * cell.quantity,
                )}
              </>
            ),
          },
        ]}
        dataSource={treatmentItems}
        pagination={false}
        bordered
        title={() => (
          <Row style={{ display: 'flex', justifyContent: 'start' }}>
            <ATitle>Procedimentos inclusos</ATitle>
          </Row>
        )}
        footer={() => {
          let sum = treatmentItems.reduce(
            (acc, current) => acc + +current.value * current.quantity,
            0,
          );
          const disct = treatmentItems.reduce(
            (acc, current) => acc + current.discount * current.quantity,
            0,
          );
          sum -= disct;

          return (
            <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
              <ATitle>Total</ATitle>
              <ATitle>{currency(sum)}</ATitle>
            </Row>
          );
        }}
      />
      {retailItems.length > 0 && (
        <Table
          rowKey="id"
          style={{ width: '100%', marginTop: 20 }}
          scroll={{ y: 240 }}
          columns={[
            {
              title: '#',
              width: 60,
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: 'Produto',
              dataIndex: 'product_name',
              width: 220,
              render: (value: string, cell: IRetailTreatmentItem) => (
                <>{`${cell.product_code} - ${value}`}</>
              ),
            },
            {
              title: 'Qtd.',
              dataIndex: 'quantity',
              width: 80,
            },
            {
              title: 'Valor UN.',
              dataIndex: 'unit_price',
              width: 130,
              render: (value: number) => <>{currency(value)}</>,
            },
            {
              title: 'Total',
              dataIndex: 'total_price',
              width: 130,
              render: (value: number) => <>{currency(value)}</>,
            },
          ]}
          dataSource={retailItems}
          pagination={false}
          bordered
          title={() => (
            <Row style={{ display: 'flex', justifyContent: 'start' }}>
              <ATitle>Venda avulsa vinculada</ATitle>
            </Row>
          )}
          footer={() => (
            <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
              <ATitle>Total venda avulsa</ATitle>
              <ATitle>{currency(retailTotal)}</ATitle>
            </Row>
          )}
        />
      )}
      <Table
        style={{ width: '100%', marginTop: 20 }}
        pagination={false}
        bordered
        columns={[
          { title: 'Componente', dataIndex: 'label' },
          {
            title: 'Valor',
            dataIndex: 'value',
            render: (value: number) => <>{currency(value)}</>,
          },
        ]}
        dataSource={[
          { key: '1', label: 'Tratamento', value: treatmentNetTotal },
          {
            key: '2',
            label: 'Produtos vinculados ao tratamento',
            value: linkedProductsTotal,
          },
          { key: '3', label: 'Venda avulsa', value: retailTotal },
          {
            key: '4',
            label: 'Total geral do fechamento',
            value: treatmentNetTotal + linkedProductsTotal + retailTotal,
          },
        ]}
        title={() => (
          <Row style={{ display: 'flex', justifyContent: 'start' }}>
            <ATitle>Resumo comercial</ATitle>
          </Row>
        )}
      />
      <Form layout="vertical" style={{ width: '100%', marginTop: 20 }}>
        <Row justify="end">
          <AButton
            type="default"
            onClick={() => navigate(-1)}
            icon={<LeftCircleOutlined />}
          >
            Voltar
          </AButton>
          {renderButtonsApprovalRefuse()}
        </Row>
      </Form>
      {renderModalConfirm()}
      {renderModalRefuse()}
    </>
  );
};

export default TreatmentApprovalOrNot;
